import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authService, normalizeUser } from "../services/auth.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setUser(null);
      setReady(true);
      return null;
    }
    try {
      const raw = await authService.getCurrentUser();
      const normalized = normalizeUser(raw);
      setUser(normalized);
      return normalized;
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setUser(null);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(
    async (credentials) => {
      const data = await authService.login(credentials);
      if (data?.user) {
        setUser(normalizeUser(data.user));
      }
      return refreshUser();
    },
    [refreshUser],
  );

  const register = useCallback(
    async (data) => {
      const res = await authService.register(data);
      if (res?.user) {
        setUser(normalizeUser(res.user));
      }
      return refreshUser();
    },
    [refreshUser],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const canEditSiteSettings = Boolean(
    user && (user.is_staff || user.is_superuser),
  );

  const value = useMemo(
    () => ({
      user,
      ready,
      login,
      logout,
      register,
      refreshUser,
      canEditSiteSettings,
      isAuthenticated: Boolean(user),
    }),
    [user, ready, login, logout, register, refreshUser, canEditSiteSettings],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
